import {
    IIndicatorNotificationTriggerRequestChild,
    IndicatorNotificationSubscriptionResponse,
    IndicatorNotificationSubscriptionUpdateRequest,
    IndicatorNotificationTriggerRequestChild,
} from '@kcftech/prometheus-client';
import { UseMutationOptions, useMutation } from '@tanstack/react-query';
import { sdClient } from 'contexts/SDKClientContext';
import { TimeSpan } from 'utils/TimeSpan';

/**
 * Configuration object for editing indicator alerts notification subscriptions.
 */
export type EditIndicatorAlertsNotificationSubscription = Readonly<{
    /**
     * Id of the notification subscription to edit.
     */
    id: string;
    /**
     * Whether or not this notification should be silenced. Defaults to `false`.
     */
    isSilenced?: boolean;
    /**
     * The types of events that will trigger notifications for this subscription.
     */
    triggers: IIndicatorNotificationTriggerRequestChild[];
    unacknowledgedPeriod: TimeSpan;
}>;

export function editIndicatorAlertsNotificationSubscription({
    id,
    isSilenced = false,
    triggers,
    unacknowledgedPeriod,
}: EditIndicatorAlertsNotificationSubscription) {
    return sdClient.updateIndicatorNotificationSubscription({
        subscriptionId: id,
        body: new IndicatorNotificationSubscriptionUpdateRequest({
            IsSilenced: isSilenced,
            UnacknowledgedPeriod: unacknowledgedPeriod.toDurationString(),
            NotificationTriggers: triggers.map(
                (trigger) => new IndicatorNotificationTriggerRequestChild(trigger),
            ),
        }),
    });
}

/** Type of data returned by mutation function */
type Data = IndicatorNotificationSubscriptionResponse;

/** The mutation options object type */
type Options = UseMutationOptions<
    Data,
    unknown,
    EditIndicatorAlertsNotificationSubscription
>;

export function useEditIndicatorAlertsNotificationSubscription(options: Options) {
    return useMutation<Data, unknown, EditIndicatorAlertsNotificationSubscription>({
        mutationFn: (args) => editIndicatorAlertsNotificationSubscription(args),
        ...options,
    });
}
